import { BindAll } from 'lodash-decorators'
import type {
  CityTypeInfo,
  IRecommendCitiesResp,
  ICityDetailResp,
  IGuildMyselfResp,
  GuildDetail,
  GuildMyself
} from '@xt/client/entities/kindred-spirit/kindred-spirit'
import { $service } from '@xt/client/experimental/request/service'
import { EnumApiTonghao } from '@xt/client/api/kindred-spirit'
import { action, observable, computed, toJS } from 'mobx'
import { reportEntryLog } from '@xt/client/utils/log'
import { getStaticJson } from '@xt/client/utils/get-static-json'
import { PagePagination } from '@xt/client/utils/infinite-loading/index'
import { getCityCascader, type RawCityItem } from '@xt/client/dataSource/china/index'

type RecommendCityParamType = {
  adcode: string
  recommend?: boolean
  page: number
  pageSize: number
  statusArr: string
}

export enum EnumJoinResponseType {
  Success = 'success',
  NoAuth = 'noAuth'
}

export enum EnumJoinResponseCode {
  NoAuth = 1352100001
}

export enum EnumCityStatus {
  /** 未创建 */
  NotInited = 0,
  /** 助力中 */
  Assistance = 1,
  /** 已创建 */
  Inited = 2
}

@BindAll()
export class Guild {
  constructor() {}

  // 分桐列表
  @observable cities: Array<CityTypeInfo> = []
  // 当前定位城市分桐
  @observable locateCity: CityTypeInfo | null = null
  // 当前成员加入的分桐
  @observable joinedCity: GuildMyself | null = null

  pagePagination = new PagePagination({
    url: EnumApiTonghao.GuildList,
    needCache: false,
    needLastId: false,
    pageSize: 20,
    mapValues: res => {
      return {
        dataSource: this.transformCityInfo(res.data)
      }
    }
  })

  @computed
  get firstCity() {
    return this.recommendCities[0]
  }

  @computed
  get recommendCities() {
    const rawCities = toJS(this.cities)

    if (!this.locateCity || !this.locateCity.id) {
      return rawCities
    }

    const targetCity = toJS(this.locateCity)
    const locateId = targetCity.id
    const existIndex = rawCities.findIndex(item => item.id === locateId)

    if (existIndex > -1) {
      const existCity = {
        ...rawCities[existIndex]
      }
      rawCities.splice(existIndex, 1)
      rawCities.splice(1, 0, existCity)
    } else {
      rawCities.splice(1, 0, targetCity)
    }

    return rawCities
  }

  transformGuild(guild: IGuildMyselfResp | ICityDetailResp): GuildMyself | GuildDetail {
    const base = {
      id: guild.id,
      count: guild.memberCount,
      adcode: guild.adcode,
      cityName: guild.regionName,
      name: guild.guildName,
      description: guild.summary,
      cover: guild.pcCoverPhoto,
      memberIds: guild.previewMemberIds ?? [],
      status: guild.status,
      recruitStatus: guild.recruitStatus,
      appletAvatar: guild.appletAvatar
    }

    if ('memberType' in guild && guild.memberType != null) {
      return { ...base, memberType: guild.memberType }
    }

    return base
  }

  transformCityInfo(city: Array<IRecommendCitiesResp>): Array<CityTypeInfo> {
    return city.map(i => {
      let subTitle: string = ''

      if (i.status === EnumCityStatus.Inited) {
        subTitle = `${i.memberCount}位桐学加入`
      } else if (i.recruitStatus === true) {
        subTitle = '筹备中...'
      } else if (i.status === EnumCityStatus.Assistance) {
        subTitle = '即将开放...'
      }

      return {
        ...this.transformGuild(i),
        subTitle
      }
    })
  }

  // 城市详情页数据初始化
  async getGuildDetail(guildId: number) {
    if (!guildId) return

    const [, res] = await $service<ICityDetailResp>({
      url: EnumApiTonghao.GuildDetail,
      params: {
        id: guildId
      }
    })

    if (res && res.id) {
      return this.transformGuild(res)
    }
  }
  // 获取推荐分桐
  public async getRecommendCities() {
    const [, res] = await $service<{
      data: Array<IRecommendCitiesResp>
      page: number
      pageSize: number
      total: number
    }>({
      url: EnumApiTonghao.RecommendCitys,
      params: {
        page: 1,
        pageSize: 8
      }
    })

    if (res && Array.isArray(res.data) && res.data.length) {
      this.setCities(this.transformCityInfo(res.data))
    }
  }
  // 获取定位分桐
  public async getLocateRecommendCities(adcode: string) {
    const params: RecommendCityParamType = {
      adcode,
      page: 1,
      pageSize: 8,
      statusArr: [EnumCityStatus.Assistance, EnumCityStatus.Inited].join(',') // 1 助力中 2 已创建
    }

    const [, res] = await $service<{
      data: Array<IRecommendCitiesResp>
      page: number
      pageSize: number
      total: number
    }>({
      url: EnumApiTonghao.GuildList,
      params
    })

    if (res && Array.isArray(res.data) && res.data.length === 1) {
      this.setLocateCity(this.transformCityInfo(res.data)[0])
    }
  }
  // 加载城市级联信息
  public async loadCascadeCities() {
    const data = await getStaticJson<RawCityItem[]>(`/mocks/geojson/cities-300.json`)

    if (!data) {
      reportEntryLog('topic.load-cascade-cities.error', {
        msg: 'fetch error'
      })
      return null
    }

    const [, res] = await $service<string[]>({
      url: EnumApiTonghao.GuildEstablished,
      method: 'GET'
    })

    const filters = res && res.length > 0 ? res : []

    return getCityCascader(data, filters)
  }
  // 获取当前用户加入的分桐
  public async getHasJoinedCity() {
    const [, res] = await $service<IGuildMyselfResp>({
      url: EnumApiTonghao.Myself
    })

    if (res && res.id) {
      this.setJoinedCity(this.transformGuild(res) as GuildMyself)
      return true
    }

    this.setJoinedCity(null)
    return false
  }

  // 加入桐学会
  public async joinGuild(guildId: number): Promise<EnumJoinResponseType | void> {
    const [err, res] = await $service<boolean>({
      url: EnumApiTonghao.GuildJoin,
      method: 'POST',
      data: {
        guildId
      }
    })

    if (err && err.errCode === EnumJoinResponseCode.NoAuth) {
      return EnumJoinResponseType.NoAuth
    }

    if (res === true) {
      return EnumJoinResponseType.Success
    }
  }

  // 创建桐学会
  public async createGuild(adcode: number) {
    const [, res] = await $service<boolean>({
      url: EnumApiTonghao.Guildcreate,
      method: 'POST',
      params: {
        adcode
      }
    })

    return res === true
  }

  @action
  setJoinedCity(city: GuildMyself | null) {
    this.joinedCity = city
  }

  @action
  setLocateCity(city: CityTypeInfo) {
    this.locateCity = city
  }

  @action
  setCities(cities: Array<CityTypeInfo>) {
    this.cities = cities
  }

  @action
  reset() {
    this.cities = []
    this.locateCity = null
    this.joinedCity = null
  }
}
